import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";

const classes = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    width: "90%",
    maxWidth: 500,
    maxHeight: "80%",
    backgroundColor: theme.color.white,
    borderRadius: 12,
    padding: 20,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 18,
    fontWeight: theme.font.weight.semibold,
    color: theme.color.backgroundText,
  },
  closeButton: {
    padding: 8,
  },
  currentSelection: {
    fontSize: 14,
    color: theme.color.primary,
    marginBottom: 16,
    fontStyle: "italic",
  },
  optionsList: {
    flex: 1,
    marginBottom: 20,
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.color.lightGrey,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: theme.color.primary,
    borderRadius: 10,
    marginRight: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  radioButtonSelected: {
    backgroundColor: theme.color.primary,
  },
  radioInner: {
    width: 8,
    height: 8,
    backgroundColor: theme.color.white,
    borderRadius: 4,
  },
  optionText: {
    flex: 1,
    fontSize: 14,
    color: theme.color.backgroundText,
  },
  optionTextSelected: {
    fontWeight: theme.font.weight.semibold,
    color: theme.color.primary,
  },
  noneOption: {
    fontStyle: "italic",
    color: theme.color.darkGrey,
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 12,
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: theme.color.lightGrey,
  },
  saveButton: {
    backgroundColor: theme.color.primary,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: theme.font.weight.semibold,
  },
  cancelButtonText: {
    color: theme.color.backgroundText,
  },
  saveButtonText: {
    color: theme.color.white,
  },
});

export default function SingleSelectModal({
  visible,
  onClose,
  onSave,
  title,
  options = [],
  selectedId = null,
  idField = "id",
  displayField = "name",
  allowNone = true,
}) {
  const [searchText, setSearchText] = useState("");
  const [tempSelected, setTempSelected] = useState(selectedId);

  useEffect(() => {
    if (visible) {
      setTempSelected(selectedId);
      setSearchText("");
    }
  }, [visible, selectedId, title, options.length, idField, displayField]);

  const filteredOptions = options.filter((option) =>
    String(option[displayField])
      .toLowerCase()
      .includes(searchText.toLowerCase()),
  );

  const handleSelectOption = useCallback((optionId) => {
    setTempSelected(optionId);
  }, []);

  const handleSave = useCallback(() => {
    onSave(tempSelected);
    onClose();
  }, [tempSelected, onSave, onClose]);

  const handleCancel = useCallback(() => {
    setTempSelected(selectedId);
    setSearchText("");
    onClose();
  }, [selectedId, onClose]);

  const getCurrentSelectionText = () => {
    if (!tempSelected) {
      return "None selected";
    }
    const selectedOption = options.find(
      (option) => option[idField] === tempSelected,
    );
    if (!selectedOption) {
      return "None selected";
    }

    if (selectedOption.name) {
      return `Selected: ${selectedOption.name}`;
    }

    return `Selected: ${selectedOption[displayField]}`;
  };

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={classes.overlay}>
        <View style={classes.modal}>
          <View style={classes.header}>
            <Text style={classes.title}>{title}</Text>
            <Pressable style={classes.closeButton} onPress={handleCancel}>
              <Icon name="close" size={24} color={theme.color.backgroundText} />
            </Pressable>
          </View>

          <Text style={classes.currentSelection}>
            {getCurrentSelectionText()}
          </Text>
          <View>
            <SearchBar
              value={searchText}
              onChangeText={setSearchText}
              placeholder={`Search ${title.toLowerCase()}...`}
            />
          </View>
          <ScrollView style={classes.optionsList}>
            {allowNone && (
              <PressableContainer
                style={classes.option}
                onPress={() => handleSelectOption(null)}
              >
                <View
                  style={[
                    classes.radioButton,
                    !tempSelected && classes.radioButtonSelected,
                  ]}
                >
                  {!tempSelected && <View style={classes.radioInner} />}
                </View>
                <Text
                  style={[
                    classes.optionText,
                    classes.noneOption,
                    !tempSelected && classes.optionTextSelected,
                  ]}
                >
                  None
                </Text>
              </PressableContainer>
            )}

            {filteredOptions.length === 0 && searchText && (
              <Text style={[classes.optionText, classes.noneOption]}>
                No {title.toLowerCase()} found matching "{searchText}"
              </Text>
            )}

            {filteredOptions.map((option) => {
              const optionId = option[idField];
              const isSelected = tempSelected === optionId;

              return (
                <PressableContainer
                  key={optionId}
                  style={classes.option}
                  onPress={() => handleSelectOption(optionId)}
                >
                  <View
                    style={[
                      classes.radioButton,
                      isSelected && classes.radioButtonSelected,
                    ]}
                  >
                    {isSelected && <View style={classes.radioInner} />}
                  </View>
                  <Text
                    style={[
                      classes.optionText,
                      isSelected && classes.optionTextSelected,
                    ]}
                  >
                    {option.firstName && option.lastName
                      ? `${option.firstName} ${option.lastName}`
                      : option[displayField]}
                  </Text>
                </PressableContainer>
              );
            })}
          </ScrollView>

          <View style={classes.buttonContainer}>
            <Pressable
              style={[classes.button, classes.cancelButton]}
              onPress={handleCancel}
            >
              <Text style={[classes.buttonText, classes.cancelButtonText]}>
                Cancel
              </Text>
            </Pressable>
            <Pressable
              style={[classes.button, classes.saveButton]}
              onPress={handleSave}
            >
              <Text style={[classes.buttonText, classes.saveButtonText]}>
                Save
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}
